import {
  CircularProgress,
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
} from '@mui/material';
import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger';

export type ButtonProps = Omit<
  MuiButtonProps,
  'variant' | 'color' | 'loadingIndicator' | 'loadingPosition'
> & {
  /**
   * primary = brand fill · secondary = line.control border, text.primary ·
   * tertiary = ink text · danger = danger.main fill. The colours live in the
   * theme's MuiButton overrides, keyed on the MUI variant/color pairs below.
   */
  variant?: ButtonVariant;
  /**
   * Disables the button and sets aria-busy. The label stays the accessible
   * name and keeps the width, but is drawn transparent behind the spinner.
   * MUI disables natively, so a focused button drops focus while loading.
   */
  loading?: boolean;
  /** With `href` (the button renders an <a>): e.g. "_blank" for a store page. */
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  /** With `href` and `target="_blank"`: "noopener noreferrer". */
  rel?: string;
};

const MUI: Record<ButtonVariant, Pick<MuiButtonProps, 'variant' | 'color'>> = {
  primary: {variant: 'contained', color: 'primary'},
  secondary: {variant: 'outlined', color: 'neutral'},
  tertiary: {variant: 'text', color: 'primary'},
  danger: {variant: 'contained', color: 'error'},
};

// MUI's default indicator is aria-labelledby the button; the button's own
// label already names it, so the spinner is decoration.
const SPINNER = <CircularProgress size={14} color="inherit" aria-hidden />;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({variant = 'primary', loading = false, ...rest}, ref) => (
    <MuiButton
      ref={ref}
      {...rest}
      {...MUI[variant]}
      loading={loading}
      loadingIndicator={SPINNER}
      aria-busy={loading || undefined}
    />
  ),
);
Button.displayName = 'Button';
